import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import MetaChip from "../../../UIToolkit/Badges/MetaChip";
import { FilterFieldRow } from "../../../UIToolkit/Filters/FilterFieldRow";
import { SystemStepper } from "../../../UIToolkit/Inputs/SystemStepper";
import { getStaffingStatusStyle } from "../../WeeklyGapView/CountBadge";
import { formatStaffCount, type GapsShiftGroup, isShownAsShort } from "../gapsDayModel";
import { coverPromptFor } from "../gapsGroupSheets";
import { useGapsCountsHidden } from "../useGapsCountsHidden";
import { POST_QUANTITY_MAX, POST_QUANTITY_MIN } from "./gapsPostShiftPrefill";

type Props = {
	/** The group the post still describes, read live — `undefined` for a post started from nothing or moved off it. */
	group: GapsShiftGroup | undefined;
	neededNurses: number;
	onNeededNursesChange: (next: number) => void;
	disabled: boolean;
	testIdPrefix: string;
};

/**
 * The post form's "How many": a form-row stepper beside the window it is answering — the group's own `filled/required`
 * and its "N short · M posted" as the home prints them (`coverPromptFor`, not netted).
 *
 * One field among the others rather than the sheet's focal card (APP-886): the count is prefilled from the shortfall and
 * rarely changed, so it no longer outweighs the job and the hours above it.
 */
export const GapsPostQuantityRow: FC<Props> = ({ group, neededNurses, onNeededNursesChange, disabled, testIdPrefix }) => {
	const { t } = useTranslation();
	const { areCountsHidden } = useGapsCountsHidden();
	const cover = group === undefined ? undefined : coverPromptFor(group);
	const isShort = cover !== undefined && isShownAsShort(cover.short);
	const staffing = group === undefined ? undefined : getStaffingStatusStyle(group.actual, group.required);

	return (
		<FilterFieldRow label={t("gaps.postSheet.howMany")} dataTestId={`${testIdPrefix}-how-many`}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
				<SystemStepper
					value={neededNurses}
					onChange={onNeededNursesChange}
					min={POST_QUANTITY_MIN}
					max={POST_QUANTITY_MAX}
					label={t("gaps.postSheet.howManyLabel")}
					disabled={disabled}
					dataTestId={`${testIdPrefix}-quantity`}
				/>
				{group !== undefined && cover !== undefined && staffing !== undefined && (
					<Box data-testid={`${testIdPrefix}-context`} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.75 }}>
						{!areCountsHidden && (
							<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
								{/* The desktop's own inks for this same cell: flagged red from two short, amber when over. */}
								<Box component="span" sx={{ color: staffing.isFlagged ? staffing.color : INK.PRIMARY }}>
									{formatStaffCount(group.actual)}
								</Box>
								<Box component="span" sx={{ color: INK.MUTED }}>{`/${formatStaffCount(group.required)}`}</Box>
							</Typography>
						)}
						<MetaChip
							size="small"
							label={isShort ? t("gaps.callIn.cover.short", { count: Number(formatStaffCount(cover.short)) }) : t("gaps.callIn.cover.covered")}
							tone={isShort ? "error" : "success"}
							dataTestId={`${testIdPrefix}-short`}
						/>
						{cover.posted > 0 && (
							<MetaChip size="small" label={t("gaps.group.posted", { count: cover.posted })} tone="success" dataTestId={`${testIdPrefix}-posted`} />
						)}
					</Box>
				)}
			</Box>
		</FilterFieldRow>
	);
};
